import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  fetchMenuItemsByType,
  fetchAllMenuItemsByType,
  createMenuItem,
  updateMenuItem,
  softDeleteMenuItem,
  softDeleteMultipleMenuItems,
  restoreMenuItem,
  fetchAllMenuItemTypes,
} from './MenuItemApi';
import toast from 'react-hot-toast';

const MenuItemView = () => {
  const [menuItemTypes, setMenuItemTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ type: '', value: '', label: '' });
  const [selectedItems, setSelectedItems] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchText, setSearchText] = useState('');
  // Load menu item types on component mount
  const loadMenuItemTypes = async () => {
    try {
      setTypesLoading(true);
      const types = await fetchAllMenuItemTypes();
      setMenuItemTypes(types);
    } catch (error) {
      toast.error('Failed to load menu item types');
      console.error('Error loading menu item types:', error);
    } finally {
      setTypesLoading(false);
    }
  };

  useEffect(() => {
    loadMenuItemTypes();
  }, []);

  const loadItems = async () => {
    if (!selectedType) return;
    
try {
    setLoading(true);
    // Use fetchAllMenuItemsByType to get both ACTIVE and PASSIVE items
    const data = await fetchAllMenuItemsByType(selectedType);
    setItems(data);
  } catch (error) {
    toast.error('Failed to load menu items');
    console.error('Error loading menu items:', error);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadItems();
    setSelectedItems([]);
    setPage(0);
  }, [selectedType]);

  useEffect(() => {
    // Filter items based on showDeleted flag
    const search = searchText.trim().toLowerCase();
     const filtered = items.filter((item) => {
      const statusMatch = showDeleted
        ? item.recordStatus === 'PASSIVE'
        : item.recordStatus === 'ACTIVE';

      if (!statusMatch) return false;

      if (!search) return true;

      const value = (item.value || '').toLowerCase();
      const label = (item.label || '').toLowerCase();

      return value.includes(search) || label.includes(search);
    });
    setFilteredItems(filtered);
    setSelectedItems([]);
    setPage(0);
  }, [items, showDeleted, searchText]);

  const handleSave = async () => {
    if (!formData.value.trim() || !formData.label.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const menuItem = {
        ...formData,
        type: selectedType,
        recordStatus: 'ACTIVE',
      };

      if (editingItem) {
        await updateMenuItem(editingItem.id, menuItem);
        toast.success('Menu item updated successfully');
      } else {
        await createMenuItem(menuItem);
        toast.success('Menu item created successfully');
      }

      setDialogOpen(false);
      setEditingItem(null);
      setFormData({ type: '', value: '', label: '' });
      loadItems();
      // Refresh types in case a new type was created
      loadMenuItemTypes();
    } catch (error) {
      toast.error('Failed to save menu item');
      console.error('Error saving menu item:', error);
    }
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await softDeleteMenuItem(id);
      toast.success('Menu item deleted successfully');
      loadItems();
    } catch (error) {
      toast.error('Failed to delete menu item');
      console.error('Error deleting menu item:', error);
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Are you sure you want to restore this item?')) return;

    try {
      await restoreMenuItem(id);
      toast.success('Menu item restored successfully');
      loadItems();
    } catch (error) {
      toast.error('Failed to restore menu item');
      console.error('Error restoring menu item:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select items to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} selected items?`)) return;

    try {
      await softDeleteMultipleMenuItems(selectedItems);
      toast.success(`${selectedItems.length} items deleted successfully`);
      setSelectedItems([]);
      loadItems();
    } catch (error) {
      toast.error('Failed to delete selected items');
      console.error('Error bulk deleting items:', error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredItems.map(item => item.id);
      setSelectedItems(newSelected);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    const selectedIndex = selectedItems.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItems, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1),
      );
    }

    setSelectedItems(newSelected);
  };

  const isSelected = (id) => selectedItems.indexOf(id) !== -1;

  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box p={3}>
      <Typography variant="h4" mb={3}>
        Menu Items Management
      </Typography>

      {/* Controls */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            select
            label="Select Menu Type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            disabled={typesLoading}
          >
            <MenuItem value="">
              {typesLoading ? 'Loading types...' : 'Select a type'}
            </MenuItem>
            {!typesLoading && menuItemTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

            <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Filter by value or label"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={!selectedType}
          />
        </Grid>
        
        <Grid item xs={12} md={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingItem(null);
              setFormData({ type: selectedType, value: '', label: '' });
              setDialogOpen(true);
            }}
            disabled={!selectedType}
            fullWidth
          >
            Add New
          </Button>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="outlined"
            color={showDeleted ? 'primary' : 'secondary'}
            onClick={() => setShowDeleted(!showDeleted)}
            disabled={!selectedType}
            fullWidth
          >
            {showDeleted ? 'Show Active' : 'Show Deleted'}
          </Button>
        </Grid>
        {!showDeleted && selectedItems.length > 0 && (
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkDelete}
              fullWidth
            >
              Delete Selected ({selectedItems.length})
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Table */}
      {selectedType && (
        <Paper>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {!showDeleted && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedItems.length > 0 && selectedItems.length < filteredItems.length}
                            checked={filteredItems.length > 0 && selectedItems.length === filteredItems.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                      )}
                      <TableCell></TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedItems.map((item) => {
                      const isItemSelected = isSelected(item.id);
                      return (
                        <TableRow key={item.id} selected={isItemSelected}>
                          {!showDeleted && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onChange={() => handleSelectItem(item.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell>{item.id}</TableCell>
                          <TableCell>{item.value}</TableCell>
                          <TableCell>{item.label}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.recordStatus}
                              color={item.recordStatus === 'ACTIVE' ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {item.recordStatus === 'ACTIVE' ? (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingItem(item);
                                      setFormData({
                                        type: selectedType,
                                        value: item.value,
                                        label: item.label,
                                      });
                                      setDialogOpen(true);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleSoftDelete(item.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Restore">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleRestore(item.id)}
                                >
                                  <RestoreIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredItems.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuItemView;